import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { SPANISH_CITIES } from './FilterBar';

const SearchHero = ({ onSearch }) => {
    const [city, setCity] = useState('');
    const [eventType, setEventType] = useState('pozo');
    const [loadingGeo, setLoadingGeo] = useState(false);
    const [distance, setDistance] = useState(50); // Default 50km
    const [userCoords, setUserCoords] = useState(null);
    const [coordsSourceCity, setCoordsSourceCity] = useState('');

    const normalizeString = (str) => {
        return str
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/-/g, " ") // Reemplazar guiones por espacios para comparación laxa
            .trim();
    };

    const handleGeolocation = () => {
        if (!navigator.geolocation) {
            toast.error('Geolocalización no soportada por su navegador');
            return;
        }

        setLoadingGeo(true);
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const { latitude, longitude } = position.coords;
                    setUserCoords({ lat: latitude, lon: longitude });
                    
                    // Reverse geocoding con OpenStreetMap
                    const response = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
                    );
                    const data = await response.json();
                    
                    console.log("Nominatim full response:", data);

                    const address = data.address || {};
                    // Lista de posibles campos donde puede estar el dato relevante, ordenados por prioridad de "ciudad"
                    const candidates = [
                        address.city,
                        address.town,
                        address.municipality,
                        address.village,
                        address.city_district,
                        address.suburb,
                        address.hamlet,
                        address.neighbourhood,
                    ].filter(Boolean); // Filtrar nulos/undefined

                    console.log("Ubicación candidatos:", candidates);

                    let bestMatch = null;

                    // Estrategia de búsqueda
                    if (candidates.length > 0) {
                        // 1. Busqueda exacta o normalizada sobre candidatos de alta prioridad (city, town, municipality, village)
                        const highPriorityCandidates = [address.city, address.town, address.municipality, address.village].filter(Boolean);
                        
                        for (const candidate of candidates) {
                            const normalizedCand = normalizeString(candidate);
                            const found = SPANISH_CITIES.find(c => {
                                const normalizedC = normalizeString(c);
                                return normalizedC === normalizedCand || 
                                       normalizedC.replace(/ /g, "") === normalizedCand.replace(/ /g, "");
                            });
                            if (found) {
                                bestMatch = found;
                                break;
                            }
                        }

                        // 2. Búsqueda por contenencia (Fuzzy)
                        if (!bestMatch) {
                             for (const candidate of candidates) {
                                const normalizedCand = normalizeString(candidate);
                                const found = SPANISH_CITIES.find(c => {
                                    const normalizedC = normalizeString(c);
                                    return normalizedC.includes(normalizedCand) || normalizedCand.includes(normalizedC);
                                });
                                if (found) {
                                    bestMatch = found;
                                    break;
                                }
                            }
                        }
                        
                         // 3. Búsqueda por tokens (si "Rivas Centro" -> match "Rivas-Vaciamadrid")
                        if (!bestMatch) {
                             for (const candidate of candidates) {
                                const tokens = normalizeString(candidate).split(" ").filter(t => t.length > 3);
                                for(const token of tokens) {
                                     const found = SPANISH_CITIES.find(c => normalizeString(c).includes(token));
                                     if(found) {
                                         bestMatch = found;
                                         break;
                                     }
                                }
                                if(bestMatch) break;
                            }
                        }
                    }

                    if (bestMatch) {
                        setCity(bestMatch);
                        setCoordsSourceCity(bestMatch);
                        toast.success(`Ubicación: ${bestMatch}`);
                    } else if (candidates.length > 0) {
                        // Si no encontramos match en nuestra lista, intentamos limpiar el primer candidato
                        // o buscar el primero que parezca una ciudad (no barrio)
                        const fallback = candidates[0];
                        setCity(fallback);
                        setCoordsSourceCity(fallback);
                        toast.success(`Ubicación detectada: ${fallback}`);
                    } else {
                         toast.error('No se pudo determinar la ciudad exacta');
                    }

                } catch (error) {
                    console.error('Error en geocoding:', error);
                    toast.error('Error al obtener la ubicación');
                } finally {
                    setLoadingGeo(false);
                }
            },
            (error) => {
                console.error('Error de geolocalización:', error);
                let msg = 'Error al obtener ubicación';
                if (error.code === 1) msg = 'Permiso de ubicación denegado';
                toast.error(msg);
                setLoadingGeo(false);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    };

    const handleSearch = async () => {
        console.log('[SearchHero] handleSearch called with eventType:', eventType);
        let normalizedCityForFilter = '';
        let currentCoords = userCoords;
        
        if (city) {
            normalizedCityForFilter = city
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .trim()
                .replace(/ /g, '_');

            // RE-GEOCODE IF:
            // 1. We don't have coordinates yet
            // OR 2. The typed city doesn't match the city that generated currentCoords
            if (!currentCoords || city.trim().toLowerCase() !== coordsSourceCity.trim().toLowerCase()) {
                try {
                    setLoadingGeo(true);
                    const query = encodeURIComponent(`${city}, Spain`);
                    const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`);
                    const data = await response.json();
                    if (data && data.length > 0) {
                        currentCoords = { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
                        setUserCoords(currentCoords);
                        setCoordsSourceCity(city); // Update source city
                    } else {
                        // If geocoding fails, we might want to clear coords to avoid using old ones from a different city
                        currentCoords = null;
                        setUserCoords(null);
                        setCoordsSourceCity('');
                    }
                } catch (error) {
                    console.error('Error geocoding city search:', error);
                    currentCoords = null;
                } finally {
                    setLoadingGeo(false);
                }
            }
        } else {
            // If city is cleared, clear coordinates as well
            setUserCoords(null);
            setCoordsSourceCity('');
            currentCoords = null;
        }

        const searchParams = {
            city: normalizedCityForFilter,
            type: eventType,
            maxDistance: currentCoords ? distance : null,
            userCoords: currentCoords
        };
        console.log('[SearchHero] Calling onSearch with params:', searchParams);
        onSearch(searchParams);
    };

    return (
        <div className="relative h-[500px] w-full flex items-center justify-center overflow-hidden">
            {/* Background Image & Overlay */}
            <div 
                className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
                style={{ 
                    backgroundImage: "url('/padel_wide.png')", // Imagen local
                    backgroundPosition: "center" 
                }}
            ></div>
            <div className="absolute inset-0 bg-slate-900/60 z-10"></div>

            {/* Content */}
            <div className="relative z-20 container mx-auto px-4 text-center">
                <h2 className="text-white text-lg md:text-xl font-medium mb-2 tracking-wide">Encuentra</h2>
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-12 tracking-tight max-w-4xl mx-auto">
                    Pozos, torneos, ligas y clubs de pádel
                </h1>

                {/* Search Bar Container */}
                <div className="bg-white p-2 rounded-2xl shadow-2xl max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-2">
                    
                    {/* Location Input Group */}
                    <div className="flex-1 w-full md:w-auto flex items-center px-4 py-2 border-b md:border-b-0 md:border-r border-gray-100 relative group">
                        <svg className="w-5 h-5 text-gray-400 mr-3 group-focus-within:text-primary-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <div className="flex-1 text-left relative">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-0.5">Ubicación</label>
                            <input 
                                type="text" 
                                placeholder="Madrid, Barcelona..." 
                                className="w-full text-gray-800 placeholder-gray-400 font-medium focus:outline-none pr-6"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            {city && (
                                <button
                                    onClick={() => {
                                        setCity('');
                                        setUserCoords(null);
                                        setCoordsSourceCity('');
                                    }}
                                    className="absolute right-0 bottom-1 text-gray-400 hover:text-red-500 transition-colors"
                                    title="Borrar ubicación"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                        <button 
                            onClick={handleGeolocation}
                            disabled={loadingGeo}
                            className={`ml-2 p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-primary-500 transition-colors ${loadingGeo ? 'animate-spin text-primary-500' : ''}`}
                            title="Usar mi ubicación actual"
                        >
                            {loadingGeo ? (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                </svg>
                            ) : (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M12 8a4 4 0 100 8 4 4 0 000-8z" /> 
                                </svg>
                            )}
                        </button>
                    </div>

                    {/* Event Type Input Group */}
                    <div className="flex-1 w-full md:w-auto flex items-center px-4 py-2 border-b md:border-b-0 md:border-r border-gray-100 relative group">
                        <svg className="w-5 h-5 text-gray-400 mr-3 group-focus-within:text-primary-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        <div className="flex-1 text-left">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-0.5">Tipo</label>
                            <select 
                                className="w-full text-gray-800 font-medium focus:outline-none bg-transparent cursor-pointer appearance-none"
                                value={eventType}
                                onChange={(e) => setEventType(e.target.value)}
                            >
                                <option value="pozo">Pozos</option>
                                <option value="tournament">Torneos</option>
                                <option value="league">Ligas</option>
                                <option value="ranking">Ranking</option>
                                <option value="club">Club</option>
                            </select>
                        </div>
                         <svg className="w-4 h-4 text-gray-400 absolute right-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>

                    {/* Distance Slider Group */}
                    <div className="flex-1 w-full md:w-auto flex flex-col px-6 py-2 relative group min-w-[200px]">
                        <div className="flex justify-between items-center mb-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Distancia</label>
                            <span className="text-xs font-black text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">{distance} km</span>
                        </div>
                        <input 
                            type="range" 
                            min="5" 
                            max="200" 
                            step="5"
                            value={distance}
                            onChange={(e) => setDistance(parseInt(e.target.value))}
                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
                        />
                        <div className="flex justify-between mt-1">
                            <span className="text-[10px] text-gray-400 font-bold">5km</span>
                            <span className="text-[10px] text-gray-400 font-bold">200km</span>
                        </div>
                        {!userCoords && (
                            <div className="absolute -top-1 right-0">
                                <span className="flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Search Button */}
                    <button 
                        onClick={handleSearch}
                        className="w-full md:w-auto bg-[#1e293b] text-white px-8 py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-[#0f172a] transition-all transform hover:scale-105 shadow-xl shadow-slate-900/20"
                    >
                        <span>BUSCAR</span>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SearchHero;
